import { AxiosError } from 'axios';

import type { ApiErrorEnvelope } from '@/types/api';

/**
 * Normalized client-side error. Every failed API call is translated into this
 * shape, so UI code branches on a stable `code` instead of inspecting raw
 * Axios/HTTP internals.
 */
export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly requestId: string | null = null,
    public readonly details: unknown[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isAuth(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/**
 * Translate any thrown value from the axios layer into an ApiError. Recognizes
 * the unified `{ error_code, message, request_id, details }` envelope; falls
 * back gracefully for network failures and non-conforming responses.
 */
export function normalizeError(error: unknown): ApiError {
  if (error instanceof ApiError) {
    return error;
  }

  if (error instanceof AxiosError) {
    const response = error.response;

    if (response) {
      const body = response.data as Partial<ApiErrorEnvelope> | undefined;
      return new ApiError(
        body?.error_code ?? 'http_error',
        body?.message ?? error.message,
        response.status,
        body?.request_id ?? null,
        body?.details ?? [],
      );
    }

    // No response → transport/network failure.
    return new ApiError('network_error', error.message, 0);
  }

  return new ApiError('unknown_error', 'An unexpected error occurred.', 0);
}
